/**
 * Dashboard Jobs tab models (spec §2.2 tab 3; W4b card C3, D-617). Pure, UI-rate (4 Hz while open): Active (accepted
 * contracts with destination, countdown, reward and a Set course target), Story (the objective line) and Boards (the
 * last-seen board of each known station with a job board, read-only: **no remote acceptance**).
 */
import { z } from 'zod'
import type { Career, ContractState } from '../../../../career/types'
import type { ContractType } from '../../../../data/m2Schemas'
import { formatCr, formatDuration } from '../../../format'
import type { Age } from '../../parts'
import type { KnownStation, StationDirectory } from '../../data'
import jobsJson from './jobs.json'

const text = z.string().min(1)
export const jobsTextSchema = z.strictObject({
  comment: z.string(),
  sections: z.strictObject({ active: text, story: text, boards: text }),
  active: z.strictObject({ empty: text, deliverAt: text, handIn: text, ready: text, expired: text, left: text, reward: text, noCourse: text, courseSet: text }),
  story: z.strictObject({ none: text, objective: text, caseFile: text }),
  boards: z.strictObject({ empty: text, accept: text, acceptAt: text, noOffers: text, offers: text }),
})
export type JobsText = z.infer<typeof jobsTextSchema>

let cached: JobsText | null = null
export function loadJobsText(): JobsText {
  cached ??= jobsTextSchema.parse(jobsJson)
  return cached
}

export function fill(template: string, values: Readonly<Record<string, string>>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => values[k] ?? m)
}

/** Where Set course goes: a known station (`setCourseTo`) or, with no known station, a system (`plotRoute`). */
export type CourseTarget = { readonly kind: 'station'; readonly id: string } | { readonly kind: 'system'; readonly id: string }

export interface ActiveJobRow {
  readonly id: string
  readonly typeLabel: string
  readonly requirement: string
  /** "Deliver at Halden Yard". */
  readonly handIn: string
  readonly timeLeft: string
  readonly expired: boolean
  readonly ready: boolean
  readonly reward: string
  readonly course: CourseTarget | null
}

/** What the Active model reads besides the career (the M2 data's names and requirement lines). */
export interface ActiveJobsDeps {
  readonly typeLabel: (t: ContractType) => string
  readonly requirement: (c: ContractState) => string
  /** A station id → display name (M2 data or the directory); null when unknown. */
  readonly stationName: (id: string) => string | null
}

/** Where a contract is handed in: the courier's destination, otherwise the issuing station. */
export function handInStation(c: ContractState): string {
  return c.type === 'courier' && c.params.destination !== undefined ? c.params.destination : c.station
}

/** The system the work happens in (Reach jobs), or null when the job is at the hand-in station. */
function workSystem(c: ContractState): string | null {
  return c.type === 'laneRepair' || c.type === 'relayParts' || c.type === 'chartSystem' ? (c.params.systemId ?? null) : null
}

function known(dir: StationDirectory, id: string): KnownStation | undefined {
  return dir.stations.find((s) => s.id === id)
}

/** Set course for a job: the work system until it is ready, then the hand-in station (a known one, or its system). */
export function courseFor(c: ContractState, dir: StationDirectory): CourseTarget | null {
  const work = workSystem(c)
  if (work !== null && c.status !== 'ready' && work !== dir.hereSystemId) return dir.jumpsTo(work) === null ? null : { kind: 'system', id: work }
  const st = known(dir, handInStation(c))
  if (st !== undefined) return { kind: 'station', id: st.id }
  return null
}

export function activeJobsModel(career: Career, gameTime: number, dir: StationDirectory, deps: ActiveJobsDeps, t: JobsText = loadJobsText()): ActiveJobRow[] {
  return career.contracts.active.map((c): ActiveJobRow => {
    const left = c.deadlineAt - gameTime
    const at = handInStation(c)
    const name = deps.stationName(at) ?? known(dir, at)?.name ?? at
    return {
      id: c.id,
      typeLabel: deps.typeLabel(c.type),
      requirement: deps.requirement(c),
      handIn: fill(t.active.deliverAt, { station: name }),
      timeLeft: left > 0 ? fill(t.active.left, { time: formatDuration(left) }) : t.active.expired,
      expired: left <= 0,
      ready: c.status === 'ready',
      reward: fill(t.active.reward, { reward: formatCr(c.reward) }),
      course: courseFor(c, dir),
    }
  })
}

export interface StoryModel {
  /** The story HUD's objective line ('' = none). */
  readonly objective: string
}

export function storyModel(objective: string): StoryModel {
  return { objective }
}

/** One offer row on a last-seen board (read-only). */
export interface BoardOfferRow {
  readonly id: string
  readonly typeLabel: string
  readonly requirement: string
  readonly reward: string
  readonly premium: boolean
}

export interface BoardRow {
  readonly stationId: string
  readonly station: string
  readonly systemId: string
  readonly age: Age
  /** Empty when never seen (the age says so) or the board had nothing. */
  readonly offers: readonly BoardOfferRow[]
  /** "Accept at Halden Yard's board". */
  readonly acceptAt: string
}

export interface BoardsDeps {
  /** The station data age (`stationAge(cfg.age, src, station, 'board', now, docked)`). */
  readonly age: (st: KnownStation) => Age
  /** The station's board offers (its last-seen cycle); called only for seen stations. */
  readonly offers: (stationId: string) => readonly BoardOfferRow[]
}

/**
 * Every known station with a job board: jumps first (this system first), then name. A never-seen station shows the
 * "no data" age and no offers (D-617: boards are what the pilot last saw; accepting needs the station).
 */
export function boardsModel(dir: StationDirectory, deps: BoardsDeps, t: JobsText = loadJobsText()): BoardRow[] {
  const rows = dir.stations
    .filter((s) => s.services.includes('contracts'))
    .map((s) => ({ s, jumps: dir.jumpsTo(s.systemId) ?? Number.POSITIVE_INFINITY }))
    .sort((a, b) => a.jumps - b.jumps || a.s.name.localeCompare(b.s.name) || (a.s.id < b.s.id ? -1 : 1))
  return rows.map(({ s }): BoardRow => {
    const age = deps.age(s)
    return {
      stationId: s.id,
      station: s.name,
      systemId: s.systemId,
      age,
      offers: age.level === 'never' ? [] : deps.offers(s.id),
      acceptAt: fill(t.boards.acceptAt, { station: s.name }),
    }
  })
}
